import React, { useState } from 'react';
import { Heart, ShieldCheck, Copy, Check, ExternalLink, Mail, Phone, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { UKGM_LINKS } from '@/lib/ui';
import { useSEO } from '@/lib/useSEO';

const BANK_ROWS = [
  { key: 'bank', label: 'Bank', value: 'HDFC Bank' },
  { key: 'title', label: 'Account Title', value: 'UNIVERSAL KRISHNA GAUKUL MISSION' },
  { key: 'no', label: 'Account Number', value: '50200117192679' },
];

export default function Donate() {
  useSEO({ title: 'Donate', description: 'Donate to Universal Krishna Gaukul Mission (UKGM). 80G tax-exempt in India. Instant online donation via Razorpay or direct bank transfer to our HDFC Bank account.' });
  const [copied, setCopied] = useState('');
  const copy = (v, k) => { navigator.clipboard?.writeText(v); setCopied(k); toast.success('Copied'); setTimeout(() => setCopied(''), 2000); };

  return (
    <div data-testid="donate-page">
      {/* HERO */}
      <section className="hero-blue text-white py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-ukgm-gold font-bold border border-ukgm-gold/40 rounded-full px-4 py-1.5">
            <Heart className="w-3.5 h-3.5" fill="currentColor" /> Support UKGM
          </div>
          <h1 className="mt-5 font-serif text-4xl md:text-6xl font-semibold leading-tight">Every donation restores dignity.</h1>
          <p className="mt-4 text-white/85 max-w-2xl mx-auto">Donations to Universal Krishna Gaukul Mission are <b>80G tax-exempt</b> in India. Choose your preferred method below — Razorpay for instant online donation, or direct bank transfer.</p>
        </div>
      </section>

      {/* METHODS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-6">
        {/* Razorpay */}
        <div className="rounded-2xl bg-white border border-ukgm-gold/25 p-8 card-lift" data-testid="donate-online-card">
          <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Online — Instant</div>
          <h2 className="mt-2 font-serif text-2xl text-ukgm-blue font-semibold">Razorpay · UPI · Cards · Netbanking</h2>
          <p className="mt-3 text-sm text-ukgm-slate">Donate securely via our verified Razorpay payment page. All major Indian banks, UPI apps and cards accepted.</p>
          <a href={UKGM_LINKS.razorpay} target="_blank" rel="noopener noreferrer" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3.5 rounded-full gold-glow" data-testid="donate-razorpay-btn">
            <ExternalLink className="w-4 h-4" /> Donate via Razorpay
          </a>
          <div className="mt-4 flex items-center gap-2 text-xs text-ukgm-slate"><Lock className="w-3.5 h-3.5 text-ukgm-gold" /> Secured by Razorpay · PCI DSS · SSL encrypted</div>
        </div>

        {/* Bank transfer */}
        <div className="rounded-2xl bg-white border border-ukgm-gold/25 p-8 card-lift" data-testid="donate-bank-card">
          <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Bank Transfer</div>
          <h2 className="mt-2 font-serif text-2xl text-ukgm-blue font-semibold">Direct NEFT / IMPS / RTGS</h2>
          <div className="mt-5 rounded-2xl border border-ukgm-gold/30 divide-y divide-ukgm-gold/20 bg-ukgm-cream">
            {BANK_ROWS.map(r => (
              <div key={r.key} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest uppercase text-ukgm-slate font-bold">{r.label}</div>
                  <div className="text-sm font-semibold text-ukgm-blue truncate mt-0.5">{r.value}</div>
                </div>
                <button onClick={() => copy(r.value, r.key)} className="text-ukgm-blue hover:text-ukgm-gold p-2" aria-label="copy" data-testid={`copy-${r.key}`}>
                  {copied === r.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-ukgm-slate leading-relaxed">After transfer, please share the transaction reference on <a href="mailto:ukgm.trust@gmail.com" className="text-ukgm-blue underline">ukgm.trust@gmail.com</a> or <a href="tel:+917447261155" className="text-ukgm-blue underline">+91 74472 61155</a> for your 80G receipt.</p>
        </div>
      </section>

      {/* 80G STRIP */}
      <section className="py-10 bg-ukgm-cream border-y border-ukgm-gold/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          <ShieldCheck className="w-10 h-10 text-ukgm-gold shrink-0" />
          <div>
            <h3 className="font-serif text-xl text-ukgm-blue font-semibold">80G Tax Exemption</h3>
            <p className="mt-1 text-sm text-ukgm-slate">Universal Krishna Gaukul Mission holds Section 80G registration under the Income Tax Act. Every donation you make qualifies you for tax exemption as per applicable law. An 80G receipt will be issued after successful contribution.</p>
          </div>
        </div>
      </section>

      {/* HELP */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-serif text-2xl text-ukgm-blue font-semibold">Need help with your donation?</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href="mailto:ukgm.trust@gmail.com" className="inline-flex items-center gap-2 bg-ukgm-blue text-white font-bold px-5 py-2.5 rounded-full"><Mail className="w-4 h-4 text-ukgm-gold" /> ukgm.trust@gmail.com</a>
          <a href="tel:+917447261155" className="inline-flex items-center gap-2 bg-white border border-ukgm-gold/40 text-ukgm-blue font-bold px-5 py-2.5 rounded-full"><Phone className="w-4 h-4 text-ukgm-gold" /> +91 74472 61155</a>
        </div>
      </section>
    </div>
  );
}
